import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { Alert, Box, Stack, Typography } from "@curves/ui-base";
import { isProductItem } from "@icoach/accountBook/createOrder/CreateOrderPage.util";
import ProductItem from "@icoach/accountBook/createOrder/components/ProductItem";
import FeeItem from "@icoach/accountBook/createOrder/components/FeeItem";
import useForkRef from "@util/hook/useForkRef";
import { formatCurrencyFn, isEmpty, isFunction, refIsRequiredError } from "@util/utils";
import OrderCartDialogBox from "@icoach/accountBook/createOrder/dialog/OrderCartDialogBox";

/**
 * 訂單購物車列表組件
 * @param cartItems - 購物車項目陣列
 * @param onUpdatePrice - 更新價格的回調函式
 * @param onUpdateQuantity - 更新數量的回調函式
 * @param onDeleteItem - 刪除項目的回調函式
 * @param onUpdateNotes - 點擊備註的回調函式
 * @param readOnly - 是否為唯讀模式（預設為 false）
 * @param showDiscount - 是否顯示折抵欄位（預設為 true）
 * @returns {JSX.Element}
 * @constructor
 */
const OrderCartList = forwardRef(({ cartItems, cartTotal, onUpdatePrice, onUpdateQuantity, onDeleteItem, onUpdateNotes, readOnly = false, showDiscount = false }, ref) => {
	const internalRef = useRef(null);
	const combinedRef = useForkRef(ref, internalRef);

	const cartItemRefs = useRef([]);

	const dialogRef = useRef(null);

	// 檢查是否有任何項目需要顯示折抵欄位
	const hasDiscountColumn = showDiscount && cartItems.some(item => item.hasOwnProperty("discountPrice"));

	const handleClickNotes = itemData => {
		const { openEditMemoDialog } = dialogRef.current || {};
		isFunction(openEditMemoDialog) && openEditMemoDialog({ itemData }, onUpdateNotes);
	};

	const handleClickDelete = item => {
		const { openConfirmDeleteItemDialog } = dialogRef.current || {};
		isFunction(openConfirmDeleteItemDialog) &&
			openConfirmDeleteItemDialog(null, () => {
				isFunction(onDeleteItem) && onDeleteItem(item);
			});
	};

	useImperativeHandle(
		combinedRef,
		() => ({
			isError: refIsRequiredError(cartItemRefs.current),
		}),
		// eslint-disable-next-line
		[],
	);

	return (
		<>
			<Box className="order-cart-list">
				<Stack className="order-cart-item-header" direction={"row"}>
					<Box className="order-cart-item-col order-cart-item-product">品項</Box>
					<Box className="order-cart-item-col order-cart-item-price">價格</Box>
					{hasDiscountColumn && <Box className="order-cart-item-col order-cart-item-discount">折抵</Box>}
					<Box className="order-cart-item-col order-cart-item-qty">數量</Box>
					<Box className="order-cart-item-col order-cart-item-total">小計</Box>
					{!readOnly && <Box className="order-cart-item-col order-cart-item-actions"></Box>}
				</Stack>
				{isEmpty(cartItems) ? (
					<Box className={"py-4"}>
						<Alert variant={"standard"} severity={"info"} color={"info"}>
							開始選購商品
						</Alert>
					</Box>
				) : (
					<>
						{cartItems.map((item, i) => {
							const isItemProduct = isProductItem(item);

							if (isItemProduct) {
								return (
									<ProductItem
										key={`product-item-${item.productID}-${item.variantID || "no-variant"}`}
										ref={el => (cartItemRefs.current[`product-item-${i}`] = el)}
										item={{
											name: item?.variantName ? `${item.productName} ${item.variantName}` : item.productName,
											price: item.assignPrice,
											fixedPrice: item.fixedPrice,
											qty: item.quantity,
											sku: item.sku,
											productType: item.productTypeText,
											desc: item.notes,
											stock: item.stock,
											isLimited: item.isLimited,
											isPriceEditable: item.isPriceEditable,
											allowDelete: item.allowDelete,
											allowZeroQuantity: item.allowZeroQuantity,
											discountPrice: item.discountPrice,
										}}
										showDiscount={hasDiscountColumn}
										onUpdatePrice={readOnly ? undefined : (e, newPrice) => onUpdatePrice(item, newPrice)}
										onUpdateQuantity={readOnly ? undefined : (e, newQty) => onUpdateQuantity(item, newQty)}
										onClickDelete={readOnly ? undefined : () => handleClickDelete(item)}
										onClickNotes={readOnly ? undefined : () => handleClickNotes(item)}
										readOnly={readOnly}
									/>
								);
							} else {
								return (
									<FeeItem
										key={`fee-item-${item.name}`}
										ref={el => (cartItemRefs.current[`fee-item-${i}`] = el)}
										item={{
											name: item?.name,
											price: item.assignPrice,
											productType: item.productTypeText,
											desc: item.notes,
											discountPrice: item.discountPrice,
										}}
										showDiscount={hasDiscountColumn}
										onUpdatePrice={readOnly ? undefined : (e, newPrice) => onUpdatePrice(item, newPrice)}
										onClickDelete={readOnly ? undefined : () => handleClickDelete(item)}
										onClickNotes={readOnly ? undefined : () => handleClickNotes(item)}
										readOnly={readOnly}
									/>
								);
							}
						})}
						{/* 購物車總計 */}
						{Boolean(cartTotal) && (
							<Stack direction="row" justifyContent="end" alignItems="center" spacing={4} className={"my-2 px-3"}>
								<Box className="font-color-3">合計</Box>
								<Typography variant={"h6"} className="pl-4 mb-0 font-weight-bold font-color-2">
									{formatCurrencyFn(cartTotal, { symbol: "$" })}
								</Typography>
							</Stack>
						)}
					</>
				)}
			</Box>
			<OrderCartDialogBox ref={dialogRef} />
		</>
	);
});

OrderCartList.displayName = "OrderCartList";

export default OrderCartList;
